import React, { useContext } from "react";
import { FiArrowRight, FiHeart } from "react-icons/fi";
import { Link } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/main.scss";

const editorialImages = {
  hero: "/img/editorial/hero-summer.jpg",
  linen: "/img/editorial/linen-dress.jpg",
  bedding: "/img/editorial/organic-bedding.jpg",
  junior: "/img/editorial/junior-fleece.jpg",
};

const popularCategories = [
  {
    title: "Shirts & Tops",
    text: "Weiche Bio-Qualitäten für jeden Tag",
    href: "/shop?q=Shirts%20%26%20Tops",
    productId: 1,
  },
  {
    title: "Hosen",
    text: "Klare Schnitte, natürlich bequem",
    href: "/shop?q=Hosen",
    productId: 7,
  },
  {
    title: "Jeans",
    text: "Denim mit weniger Wasserverbrauch",
    href: "/shop?q=Jeans",
    productId: 6,
  },
  {
    title: "Strickjacken",
    text: "Leichte Lagen aus Naturfasern",
    href: "/shop?q=Strickjacken",
    productId: 5,
  },
].map((category) => ({
  ...category,
  image:
    productData.find((product) => product.id === category.productId)?.image ||
    productData[0]?.image,
}));

const markedNewProducts = productData.filter((product) =>
  product.badges?.some((badge) => badge.toLocaleLowerCase("de-DE") === "neu")
);

const newProducts = [
  ...markedNewProducts,
  ...productData.filter(
    (product) => !markedNewProducts.some((item) => item.id === product.id)
  ),
].slice(0, 4);

const materialNotes = [
  {
    number: "01",
    title: "Bio-Baumwolle",
    text: "Ohne chemisch-synthetische Pestizide angebaut.",
  },
  {
    number: "02",
    title: "Leinen",
    text: "Kühlend, robust und von Natur aus charaktervoll.",
  },
  {
    number: "03",
    title: "Wolle",
    text: "Wärmend, atmungsaktiv und sorgfältig ausgewählt.",
  },
];

const priceFormatter = new Intl.NumberFormat(siteConfig.locale, {
  style: "currency",
  currency: siteConfig.currency,
});

function Main() {
  const cartState = useContext(CartContext) || {};
  const wishlistItems = Array.isArray(cartState.wishlistItems)
    ? cartState.wishlistItems
    : [];
  const toggleWishlist = cartState.toggleWishlist || (() => {});

  return (
    <main className="main-page">
      <section className="main-page__hero" aria-labelledby="summer-heading">
        <img
          src={editorialImages.hero}
          alt="Menschen in leichter Naturmode auf einer sommerlichen Wiese"
          width="1727"
          height="911"
          loading="eager"
          fetchpriority="high"
          decoding="async"
        />
        <div className="main-page__hero-content">
          <span className="main-page__eyebrow">Natürlich durch den Sommer</span>
          <h1 id="summer-heading">Sommer, ganz natürlich.</h1>
          <p>
            Luftiges Leinen, weiche Bio-Baumwolle und Farben, die sich so
            entspannt anfühlen wie die Saison selbst.
          </p>
          <div className="main-page__hero-actions">
            <Link
              className="main-page__button main-page__button--solid"
              to="/shop?category=Damen"
            >
              Damen entdecken
              <FiArrowRight aria-hidden="true" />
            </Link>
            <Link
              className="main-page__button main-page__button--text"
              to="/shop?category=Herren"
            >
              Herren entdecken
            </Link>
          </div>
        </div>
      </section>

      <section className="main-page__promotion" aria-labelledby="sale-heading">
        <div className="main-page__promotion-inner">
          <div>
            <span>Nur für kurze Zeit</span>
            <h2 id="sale-heading">Lieblingsstücke im Sommerangebot*</h2>
          </div>
          <nav aria-label="Sale nach Sortiment">
            {siteConfig.nav.map((item) => (
              <Link
                key={item.category}
                to={
                  item.category === "Sale"
                    ? item.href
                    : `/shop?category=Sale&q=${encodeURIComponent(item.category)}`
                }
              >
                {item.label}
                <FiArrowRight aria-hidden="true" />
              </Link>
            ))}
          </nav>
        </div>
      </section>

      <section
        className="main-page__editorials"
        aria-label="Aktuelle Kollektionen"
      >
        <article className="main-page__editorial main-page__editorial--linen">
          <Link
            className="main-page__editorial-media"
            to="/shop?q=Leinen"
            aria-label="Leinen-Kollektion entdecken"
          >
            <img
              src={editorialImages.linen}
              alt="Frau in einem salbeigrünen Leinenkleid"
              width="1024"
              height="1536"
              loading="lazy"
              decoding="async"
            />
          </Link>
          <div className="main-page__editorial-copy">
            <span className="main-page__eyebrow">Im Einklang mit der Natur</span>
            <h2>Leinen, neu gedacht</h2>
            <p>
              Die natürliche Premium-Faser zeigt sich in puristischen Formen
              und ruhigen Tönen – angenehm kühl und gemacht für viele Sommer.
            </p>
            <Link className="main-page__inline-link" to="/shop?q=Leinen">
              Leinen entdecken
              <FiArrowRight aria-hidden="true" />
            </Link>
          </div>
        </article>

        <article className="main-page__editorial main-page__editorial--home">
          <Link
            className="main-page__editorial-media"
            to="/shop?category=Home"
            aria-label="Natürliche Bettwäsche entdecken"
          >
            <img
              src={editorialImages.bedding}
              alt="Natürliches Schlafzimmer mit Bettwäsche in warmen Erdtönen"
              width="1536"
              height="1024"
              loading="lazy"
              decoding="async"
            />
          </Link>
          <div className="main-page__editorial-copy">
            <span className="main-page__eyebrow">Sanft schlafen</span>
            <h2>Weiche Sommergeschichten</h2>
            <p>
              Atmungsaktive Bettwäsche aus Bio-Baumwolle bringt natürliche
              Ruhe, feine Struktur und ein gutes Gefühl ins Schlafzimmer.
            </p>
            <Link
              className="main-page__inline-link"
              to="/shop?category=Home"
            >
              Bettwäsche entdecken
              <FiArrowRight aria-hidden="true" />
            </Link>
          </div>
        </article>
      </section>

      <section className="main-page__categories" aria-labelledby="category-heading">
        <header className="main-page__section-heading">
          <span className="main-page__eyebrow">Für jeden Tag</span>
          <h2 id="category-heading">Beliebte Kategorien</h2>
          <p>
            Zeitlose Lieblingsstücke, hochwertige Naturfasern und Passformen,
            die Bewegungsfreiheit lassen.
          </p>
        </header>
        <div className="main-page__category-grid">
          {popularCategories.map((category) => (
            <Link
              className="main-page__category-card"
              key={category.title}
              to={category.href}
            >
              <span className="main-page__category-image">
                <img
                  src={category.image}
                  alt=""
                  width="1024"
                  height="1365"
                  loading="lazy"
                  decoding="async"
                />
              </span>
              <span className="main-page__category-copy">
                <span>
                  <strong>{category.title}</strong>
                  <small>{category.text}</small>
                </span>
                <FiArrowRight aria-hidden="true" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="main-page__materials" aria-labelledby="materials-heading">
        <div className="main-page__materials-copy">
          <span className="main-page__eyebrow">{siteConfig.brandName}</span>
          <h2 id="materials-heading">
            Von der Faser bis zu deinem Lieblingsstück
          </h2>
          <p>{siteConfig.description}</p>
          <p>
            Wir wählen Materialien mit Sorgfalt, denken Herkunft transparent
            und gestalten so, dass du jedes Teil lange und gerne tragen kannst.
          </p>
          <Link className="main-page__inline-link" to="/about">
            Unsere Haltung
            <FiArrowRight aria-hidden="true" />
          </Link>
        </div>
        <div className="main-page__material-notes" aria-label="Unsere Naturfasern">
          {materialNotes.map((material) => (
            <article key={material.number}>
              <span>{material.number}</span>
              <h3>{material.title}</h3>
              <p>{material.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="main-page__new" aria-labelledby="new-heading">
        <header className="main-page__section-heading main-page__section-heading--row">
          <div>
            <span className="main-page__eyebrow">Gerade angekommen</span>
            <h2 id="new-heading">Neuheiten</h2>
          </div>
          <Link className="main-page__inline-link" to="/shop?q=Neu">
            Alle Neuheiten
            <FiArrowRight aria-hidden="true" />
          </Link>
        </header>

        <div className="main-page__product-rail" role="list">
          {newProducts.map((product) => {
            const isFavorite = wishlistItems.includes(product.id);

            return (
              <article className="main-page__product-card" key={product.id} role="listitem">
                <div className="main-page__product-media">
                  <Link
                    to={`/shopDetails/${product.id}`}
                    aria-label={`${product.name} ansehen`}
                  >
                    <img
                      src={product.image}
                      alt={product.name}
                      width="1024"
                      height="1365"
                      loading="lazy"
                      decoding="async"
                    />
                  </Link>
                  {product.badges?.[0] && (
                    <span className="main-page__product-badge">
                      {product.badges[0]}
                    </span>
                  )}
                  <button
                    type="button"
                    className={`main-page__wishlist ${
                      isFavorite ? "is-active" : ""
                    }`}
                    aria-label={
                      isFavorite
                        ? `${product.name} aus der Merkliste entfernen`
                        : `${product.name} zur Merkliste hinzufügen`
                    }
                    aria-pressed={isFavorite}
                    onClick={() => toggleWishlist(product.id)}
                  >
                    <FiHeart aria-hidden="true" />
                  </button>
                </div>
                <div className="main-page__product-info">
                  <span>{product.subcategory}</span>
                  <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
                  <div
                    className={`main-page__price-row${
                      Number(product.originalPrice) > Number(product.price)
                        ? " is-sale"
                        : ""
                    }`}
                  >
                    <strong>{priceFormatter.format(Number(product.price))}</strong>
                    {Number(product.originalPrice) > Number(product.price) && (
                      <del>
                        {priceFormatter.format(Number(product.originalPrice))}
                      </del>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="main-page__junior" aria-labelledby="junior-heading">
        <div className="main-page__junior-media">
          <img
            src={editorialImages.junior}
            alt="Zwei Kinder in weicher Fleecekleidung spielen draußen"
            width="1024"
            height="1536"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="main-page__junior-copy">
          <span className="main-page__eyebrow">Bereit für Kita & Schule</span>
          <h2 id="junior-heading">Das kuschelige Starter-Set</h2>
          <p>
            Neue Jacken und Lieblingsstücke aus weichen Naturfasern. Warm,
            pflegeleicht und bereit für kleine Menschen, die Großes vorhaben.
          </p>
          <Link
            className="main-page__button main-page__button--solid"
            to="/shop?category=Junior"
          >
            Junior entdecken
            <FiArrowRight aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="main-page__friends" aria-labelledby="friends-heading">
        <span className="main-page__eyebrow">Gemeinsam mehr bewegen</span>
        <h2 id="friends-heading">{siteConfig.brandName} friends</h2>
        <p>
          Werde Teil unserer Gemeinschaft und erhalte für deine Treue exklusive
          Vorteile, Inspiration und frühzeitigen Zugang zu besonderen Aktionen.
        </p>
          <Link className="main-page__inline-link" to="/about">
          Mehr über die Vorteile erfahren
          <FiArrowRight aria-hidden="true" />
        </Link>
      </section>
    </main>
  );
}

export default Main;
